import { cellToBoundary } from 'h3-js';

// Map rendering for an org's area dataset (one metric at a time).
// Cells are coloured by percentile rank rather than raw value, so a few very
// large cells don't wash every other cell out to the lowest colour.

// [r, g, b] stops from low to high (amber → violet).
const RAMP = [
  [254, 240, 217],
  [253, 204, 138],
  [252, 141, 89],
  [215, 48, 31],
  [127, 0, 0],
];

export function rampColor(rank, alpha = 180) {
  const t = Math.min(1, Math.max(0, Number.isFinite(rank) ? rank : 0)) * (RAMP.length - 1);
  const i = Math.min(RAMP.length - 2, Math.floor(t));
  const f = t - i;
  const c = RAMP[i].map((v, k) => Math.round(v + (RAMP[i + 1][k] - v) * f));
  return [...c, alpha];
}

export const RAMP_CSS = RAMP.map(([r, g, b]) => `rgb(${r}, ${g}, ${b})`);

// Percentile rank in [0, 1] for each value; ties share a rank.
export function percentileRanks(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  if (n <= 1) return values.map(() => 1);
  const firstIndex = new Map();
  sorted.forEach((v, i) => { if (!firstIndex.has(v)) firstIndex.set(v, i); });
  return values.map((v) => firstIndex.get(v) / (n - 1));
}

// GeoJSON polygons for /api/datasets/{id}/values cells.
export function datasetGeoJson(cells) {
  const list = (cells || []).filter((c) => c && c.h3_cell && Number.isFinite(c.value));
  const ranks = percentileRanks(list.map((c) => c.value));
  return {
    type: 'FeatureCollection',
    features: list.map((c, i) => ({
      type: 'Feature',
      properties: { h3_cell: c.h3_cell, value: c.value, rank: ranks[i] },
      geometry: { type: 'Polygon', coordinates: [closeRing(cellToBoundary(c.h3_cell, true))] },
    })),
  };
}

function closeRing(ring) {
  if (!ring.length) return ring;
  const [a, b] = [ring[0], ring[ring.length - 1]];
  return a[0] === b[0] && a[1] === b[1] ? ring : [...ring, a];
}

// "Residents (people)" style label for a metric.
export function metricLabel(metric) {
  if (!metric) return '';
  return metric.unit ? `${metric.label} (${metric.unit})` : metric.label;
}
